import { cn } from "../../utils/cn";
import { Card } from "../ui/Card";
import { Link } from "react-router-dom";

export interface SectionHeaderProps {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
  className?: string;
}

export function SectionHeader({
  id,
  eyebrow,
  title,
  description,
  align = "left",
  className,
}: SectionHeaderProps) {
  return (
    <header
      className={cn(
        "section-heading max-w-3xl",
        align === "center" ? "text-center mx-auto" : "",
        className,
      )}
    >
      {eyebrow && (
        <span className="label text-accent mb-3 inline-block animate-slide-up">
          {eyebrow}
        </span>
      )}
      <h2 id={id} className="section-title text-text-primary mb-4">
        {title}
      </h2>
      {description && (
        <p className="body-lg text-text-secondary animate-slide-up stagger-2">
          {description}
        </p>
      )}
    </header>
  );
}

export interface CTAProps {
  primaryText: string;
  primaryHref: string;
  secondaryText?: string;
  secondaryHref?: string;
  variant?: "primary" | "inverse";
  align?: "left" | "center";
  className?: string;
}

export function CTA({
  primaryText,
  primaryHref,
  secondaryText,
  secondaryHref,
  variant = "primary",
  align = "left",
  className,
}: CTAProps) {
  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row items-start sm:items-center gap-4",
        align === "center" ? "justify-center" : "",
        className,
      )}
    >
      <Link
        to={primaryHref}
        className={cn(
          "btn btn-primary btn-lg",
          variant === "inverse" && "bg-white text-black hover:bg-gray-100",
        )}
      >
        {primaryText}
      </Link>
      {secondaryText && secondaryHref && (
        <Link to={secondaryHref} className="btn btn-secondary btn-lg">
          {secondaryText}
        </Link>
      )}
    </div>
  );
}

export interface FeatureCardProps {
  icon?: React.ReactNode;
  number?: string;
  title: string;
  description: string;
  href?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function FeatureCard({
  icon,
  number,
  title,
  description,
  href,
  className,
  style,
}: FeatureCardProps) {
  const content = (
    <Card
      variant="default"
      padding="lg"
      className={cn("h-full flex flex-col", className)}
      style={style}
    >
      {number && (
        <span className="label text-accent mb-3" aria-hidden="true">
          {number}
        </span>
      )}
      {icon && (
        <div
          className="w-12 h-12 rounded-lg bg-accent-muted flex items-center justify-center mb-4 text-accent"
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <h3 className="h3 text-text-primary mb-2">{title}</h3>
      <p className="body text-text-secondary flex-1">{description}</p>
      {href && (
        <span className="link-underline mt-4 inline-flex items-center gap-1 font-medium text-accent">
          Learn more
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      )}
    </Card>
  );

  if (href) {
    return (
      <Link to={href} className="feature-link block h-full">
        {content}
      </Link>
    );
  }

  return content;
}

export interface StatProps {
  value: string;
  label: string;
  description?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function Stat({
  value,
  label,
  description,
  className,
  style,
}: StatProps) {
  return (
    <div className={cn("text-center", className)} style={style}>
      <div
        className="display-md font-bold text-text-primary mb-1"
        aria-label={label}
      >
        {value}
      </div>
      <div className="label text-text-secondary uppercase tracking-wider mb-1">
        {label}
      </div>
      {description && <p className="caption text-text-muted">{description}</p>}
    </div>
  );
}

export interface DividerProps {
  className?: string;
}

export function Divider({ className }: DividerProps) {
  return (
    <hr
      className={cn("divider my-12 md:my-16", className)}
      aria-hidden="true"
    />
  );
}
